import { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useTheme } from '../../hooks/useTheme'
import { setLanguage as setI18nLanguage, default as i18n } from '../../i18n'
import '../../styles/animations.css'
import { AppSidebar } from './AppSidebar'
import { SidebarDock } from './SidebarDock'
import { DashboardHeader } from './DashboardHeader'
import { MobileNotificationDrawer } from './MobileNotificationDrawer'
import { MobileBottomNav } from './MobileBottomNav'
import { ViewAsBadge, VIEW_AS_BANNER_HEIGHT } from './ViewAsBadge'
import { HouseAdSlot } from '../ads/HouseAdSlot.jsx' // [HOTFIX-FINAL-2 З6]

function useViewport() {
    const [viewport, setViewport] = useState({
        isMobile: window.innerWidth < 1024,
        isDesktop: window.innerWidth >= 1024,
    })

    useEffect(() => {
        const handleResize = () => {
            setViewport({
                isMobile: window.innerWidth < 1024,
                isDesktop: window.innerWidth >= 1024,
            })
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    return viewport
}

export function DashboardShell({
    userRole,
    user,
    menuItems,
    cabinets = [],
    notifications = [],
    subscriptions = [],
    onLogout,
    onMarkNotificationRead,
    onMarkAllNotificationsRead,
    onDeleteNotification,
    children,
    title,
    unreadCount = 0,
}) {
    const location = useLocation()
    const viewport = useViewport()
    const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1024
    const { updateUser } = useAuth()
    // [HOTFIX-FINAL-2 З6] нижняя плашка house-ads: только <768px, не в чате, не для команды
    const showHouseAdBottomBar = windowWidth < 768
        && !location.pathname.startsWith('/creative-hub')
        && !['owner', 'admin', 'staff'].includes(user?.role)
    // [VIEW-AS-PERSIST] плашка активна → контенту нужен отступ сверху, чтобы бейдж его не перекрывал
    const viewAsActive = user?.realRole === 'owner' && user?.role && user.role !== 'owner'
    // [CHAT-PRO-REWORK] Люкс-хаб (chat-режим) — точная высотка вьюпорта без скролла страницы:
    // main h-100dvh flex-колонка, обёртка и контент flex-1 min-h-0 (иначе чат вылезал за экран
    // и страница сама скроллилась — шапка уезжала под глобальный хедер)
    // [CHAT-PRO-FIX З1] ВСЕ режимы хаба люксовые → весь /creative-hub (иначе analyzer/viral скроллились)
    const isLuxeChat = location.pathname.startsWith('/creative-hub')
    const { theme, appliedTheme, setTheme, toggleTheme } = useTheme()
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [mobileNotifOpen, setMobileNotifOpen] = useState(false)
    const [language, setLanguage] = useState(() => {
        return user?.preferences?.language || localStorage.getItem('app_language') || 'ru'
    })

    // [P16] Swipe-to-close sidebar drawer on mobile
    const touchStartX = useRef(0)
    const handleSidebarTouchStart = (e) => {
        touchStartX.current = e.touches?.[0]?.clientX ?? e.clientX
    }
    const handleSidebarTouchMove = (e) => {
        if (!viewport.isMobile || !sidebarOpen) return
        const currentX = e.touches?.[0]?.clientX ?? e.clientX
        if (touchStartX.current - currentX > 80) {
            setSidebarOpen(false)
        }
    }

    useEffect(() => {
        setSidebarOpen(false)
        setMobileNotifOpen(false)
    }, [location.pathname])

    useEffect(() => {
        const i18nLang = i18n.language?.split('-')[0]
        if (i18nLang && ['ru', 'en'].includes(i18nLang) && i18nLang !== language) {
            setLanguage(i18nLang)
        }
    }, [language])

    // Initial language sync from user preferences (once)
    const languageSynced = useRef(false)
    useEffect(() => {
        if (languageSynced.current) return
        const prefsLang = user?.preferences?.language
        if (prefsLang && prefsLang !== language) {
            setLanguage(prefsLang)
        }
        languageSynced.current = true
    }, [user?.preferences?.language, language])

    const handleLanguageChange = useCallback((nextLanguage) => {
        if (!nextLanguage || nextLanguage === language) return
        localStorage.setItem('app_language', nextLanguage)
        setI18nLanguage(nextLanguage)
        const prefs = user?.preferences || {}
        if (prefs.language !== nextLanguage) {
            updateUser({ preferences: { ...prefs, language: nextLanguage } })
        }
        setLanguage(nextLanguage)
    }, [language, user?.preferences, updateUser])

    // Initial theme sync from user preferences (once)
    const themeSynced = useRef(false)
    useEffect(() => {
        if (themeSynced.current) return
        const prefsTheme = user?.preferences?.theme
        if (prefsTheme && ['light', 'dark'].includes(prefsTheme) && prefsTheme !== theme) {
            setTheme(prefsTheme)
        }
        themeSynced.current = true
    }, [user?.preferences?.theme, theme, setTheme])

    const handleThemeToggle = useCallback(() => {
        const next = appliedTheme === 'dark' ? 'light' : 'dark'
        setTheme(next)
        const prefs = user?.preferences || {}
        if (prefs.theme !== next) {
            updateUser({ preferences: { ...prefs, theme: next } })
        }
    }, [appliedTheme, setTheme, updateUser, user?.preferences]) // [P16-FIX] sync toggle with appliedTheme

    useEffect(() => {
        if (viewport.isMobile) {
            document.body.style.overflow = sidebarOpen || mobileNotifOpen ? 'hidden' : ''
        }
        return () => { document.body.style.overflow = '' }
    }, [sidebarOpen, mobileNotifOpen, viewport.isMobile])

    return (
        <div className="flex min-h-screen bg-[var(--bg)] text-[var(--text)] transition-colors duration-200 relative animated-gradient-bg">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && viewport.isMobile && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[59]"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Mobile notifications overlay */}
            {mobileNotifOpen && viewport.isMobile && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[59]"
                    onClick={() => setMobileNotifOpen(false)}
                />
            )}

            {/* Desktop sidebar: collapsed 1024-1439, full ≥1440 */}
            {!viewport.isMobile && (
                <aside
                    className="fixed top-0 left-0 h-full z-30 transition-all duration-200"
                    style={viewAsActive ? { top: `calc(${VIEW_AS_BANNER_HEIGHT}px + env(safe-area-inset-top, 0px))` } : undefined}
                >
                    <AppSidebar
                        userRole={userRole}
                        menuItems={menuItems}
                        cabinets={cabinets}
                        notifications={notifications}
                        subscriptions={subscriptions}
                        user={user}
                        onLogout={onLogout}
                        isMobile={false}
                    />
                </aside>
            )}

            {/* Mobile sidebar drawer */}
            {viewport.isMobile && (
                <aside
                    onTouchStart={handleSidebarTouchStart}
                    onTouchMove={handleSidebarTouchMove}
                    className={`
                        fixed top-0 left-0 h-full z-[60] overflow-y-auto
                        transform transition-transform duration-200 ease-out
                        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
                    `}
                    style={viewAsActive ? { top: `calc(${VIEW_AS_BANNER_HEIGHT}px + env(safe-area-inset-top, 0px))` } : undefined}
                >
                    <AppSidebar
                        userRole={userRole}
                        menuItems={menuItems}
                        cabinets={cabinets}
                        notifications={notifications}
                        subscriptions={subscriptions}
                        user={user}
                        onLogout={onLogout}
                        onClose={() => setSidebarOpen(false)}
                        isMobile={true}
                    />
                </aside>
            )}

            {/* Mobile notifications drawer */}
            <MobileNotificationDrawer
                isOpen={mobileNotifOpen}
                onClose={() => setMobileNotifOpen(false)}
                notifications={notifications}
                onMarkRead={onMarkNotificationRead}
                onMarkAllRead={onMarkAllNotificationsRead}
                onDelete={onDeleteNotification}
            />

            {/* [VIEW-AS-PERSIST] плашка «Ты смотришь как …» — на каждом экране, поверх хедера */}
            <ViewAsBadge />

            {/* Main content */}
            <main
                className={`flex-1 w-full overflow-x-hidden ${isLuxeChat ? 'h-[100dvh] flex flex-col' : 'min-h-screen'} ${!viewport.isMobile ? 'pt-20' : 'pt-16'}`}
                style={{
                    ...(!viewport.isMobile ? { paddingLeft: windowWidth >= 1440 ? '280px' : '72px' } : {}),
                    ...(viewAsActive ? { paddingTop: `calc(${viewport.isMobile ? '4rem' : '5rem'} + ${VIEW_AS_BANNER_HEIGHT}px + env(safe-area-inset-top, 0px))` } : {}),
                }}
            >
                <DashboardHeader
                    topOffset={viewAsActive ? `calc(${VIEW_AS_BANNER_HEIGHT}px + env(safe-area-inset-top, 0px))` : undefined}
                    title={title}
                    user={user}
                    notifications={notifications}
                    unreadCount={unreadCount}
                    language={language}
                    onLanguageChange={handleLanguageChange}
                    isDark={appliedTheme === 'dark'}
                    onThemeToggle={handleThemeToggle}
                    onMenuClick={() => setSidebarOpen(true)}
                    onNotificationsClick={() => setMobileNotifOpen(true)}
                    onMarkNotificationRead={onMarkNotificationRead}
                    onMarkAllNotificationsRead={onMarkAllNotificationsRead}
                    onDeleteNotification={onDeleteNotification}
                />

                {/* [CHAT-PRO-REWORK] на /creative-hub/chat обёртка — flex-1 min-h-0 без pb под навбар
                    (своя нижняя нав и так скрыта в chat-режиме): чат занимает ровно оставшуюся высоту,
                    страница не скроллится и шапка Люкс-хаба не уезжает под глобальный хедер */}
                <div className={`${viewport.isMobile ? `px-3 py-4 ${isLuxeChat ? 'pb-4' : showHouseAdBottomBar ? 'pb-40' : 'pb-24'}` : viewport.isDesktop ? 'px-6 lg:px-8 py-6' : 'px-4 py-5'} ${isLuxeChat ? 'flex-1 min-h-0 flex flex-col' : ''}`}>
                    {/* [HOTFIX-FINAL-2 З6] house-ads мини-пилюля: сайдбар свёрнут/скрыт (768–1439px),
                        карточке места нет — пилюля над контентом не сдвигает инпуты */}
                    <div className="hidden md:flex min-[1440px]:hidden justify-center mb-3 shrink-0">
                        <HouseAdSlot slot="tablet-pill" variant="pill" />
                    </div>
                    {isLuxeChat ? <div className="flex-1 min-h-0 flex flex-col">{children}</div> : children}
                </div>
            </main>

            {/* [HOTFIX-FINAL-2 З6] house-ads нижняя плашка <768px над safe-area (сворачиваемая).
                На /creative-hub не показываем: там топ-баннер + свой FAB/навбар — плашка перекрыла бы их */}
            {showHouseAdBottomBar && (
                <div className="fixed left-3 right-3 z-40" style={{ bottom: 'calc(4.5rem + env(safe-area-inset-bottom, 0px))' }}>
                    <HouseAdSlot slot="mobile-bottom" variant="bottombar" />
                </div>
            )}

            {/* [UI-VERIFY] на /creative-hub своя нижняя панель режимов — две панели перекрывали кнопки друг друга */}
            {!location.pathname.startsWith('/creative-hub') && (
                <MobileBottomNav
                    userRole={userRole}
                    onHaptic={() => navigator.vibrate?.(50)}
                />
            )}
        </div>
    )
}
